import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, ChevronDown } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToForm = () => {
    const formSection = document.getElementById('contact-form-section');
    if (formSection) {
      const headerHeight = 65;
      const elementTop = formSection.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementTop - headerHeight, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to submit form');
      }
      
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="page-wrapper page-maintenance page-contact-maintenance">
      <Header />
      
      {/* Maintenance Header Section */}
      <section className="maintenance-section contact-maintenance-section">
        <ScrollReveal>
          <div className="maintenance-content">
            <h1 className="maintenance-title">Contact</h1>
            <div className="maintenance-divider"></div>
            <p className="maintenance-message">
              We&apos;re updating our website.
            </p>
            <p className="maintenance-submessage">
              Tell us about your project.
            </p>
            <div className="scroll-arrow-container" onClick={scrollToForm}>
              <ChevronDown className="scroll-arrow" size={32} />
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* Contact Form Section - Dark Theme */}
      <section id="contact-form-section" className="contact-page-form-section">
        <ScrollReveal>
          <div className="contact-page-form-wrapper">
            <h2 className="section-title-inline contact-page-form-title">
              <span className="title-light">GET IN</span> <span className="title-bold">TOUCH.</span>
            </h2>
            <p className="contact-page-intro-text">
              We&apos;d love to hear about your project. Let&apos;s start a conversation.
            </p>
            <form onSubmit={handleSubmit} className="contact-page-form">
              <div className="contact-form-row">
                <Input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="contact-page-input"
                  disabled={isSubmitting}
                />
                <Input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="contact-page-input"
                  disabled={isSubmitting}
                />
              </div>
              <Textarea
                name="message"
                placeholder="Tell us about your project"
                value={formData.message}
                onChange={handleChange}
                required
                className="contact-page-textarea"
                rows={5}
                disabled={isSubmitting}
              />
              <Button type="submit" className="contact-page-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
            <div className="contact-details-footer contact-page-details">
              <span className="contact-details-label">Or reach us directly</span>
              <div className="contact-details-links">
                <a href="mailto:enquiries@domusliving.net" className="contact-detail-link">
                  <Mail size={16} strokeWidth={1.5} />
                  <span>enquiries@domusliving.net</span>
                </a>
                <span className="contact-detail-divider">|</span>
                <a href="tel:0417012601" className="contact-detail-link">
                  <Phone size={16} strokeWidth={1.5} />
                  <span>0417 012 601</span>
                </a>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>

      <Footer />
    </div>
  );
};
